import {
  ArrowDownIcon,
  ArrowUpIcon,
  Check,
  EditIcon,
  MoreHorizontal,
  Plus,
  Search,
  X,
} from "lucide-react-native";
import React, { memo } from "react";

import type { BoardSortDirection } from "@/features/player-kanban/constants/boardSort";
import type { KanbanColors } from "@/features/player-kanban/constants/colors";
import type { BoardViewMode } from "@/features/player-kanban/types/types";
import BoardRoleBadge from "@/features/player-kanban/components/molecules/BoardRoleBadge";
import BoardViewToggle from "@/features/player-kanban/components/molecules/BoardViewToggle";
import BoardTabs, {
  BoardInfo,
} from "@/features/player-kanban/components/organisms/BoardTabs/BoardTabsContainer";
import PipelineChipsRow from "@/features/player-kanban/components/molecules/PipelineChipsRow";
import PipelineActivityLogButton from "@/features/player-kanban/components/organisms/PipelineActivityLogButton";
import ReviewQueueButton from "@/features/player-kanban/components/organisms/ReviewQueueButton";
import RulesPanelButton from "@/features/player-kanban/components/organisms/RulesPanelButton";

import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import DesktopDropdownMenu, {
  type DesktopDropdownMenuItem,
} from "@/components/molecules/DesktopDropdownMenu";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";

import { UserKanbanBoardRole } from "@/generated/graphql";
import {
  TEST_ID_BOARD_TOOLBAR,
  TEST_ID_PIPELINE_CATEGORY_CHIP,
  TEST_ID_PLAYER_KANBAN,
} from "e2e/testId";

/**
 * Board-rename input surface style.
 * design-system: dynamic-style waiver — data-driven value (kanban theme palette)
 * @param colors - Resolved kanban theme colors
 */
const renameInputStyle = (colors: KanbanColors) => ({
  backgroundColor: colors.columnBackground,
  borderColor: colors.primary,
});

/**
 * Text-color style for a data-driven theme color.
 * design-system: dynamic-style waiver — data-driven value (theme color)
 * @param color - Resolved color string
 */
const textColorStyle = (color: string) => ({ color });

/**
 * Props for the TopPanelView component
 */
interface TopPanelViewProps {
  pipelineName: string;
  isEditing: boolean;
  editValue: string;
  boards: BoardInfo[];
  /** SE-6575: whether boards exist beyond those loaded (page cap / failed page). */
  hasUnreachableBoards?: boolean;
  isRosterBoard?: boolean;
  /**
   * SE-5489: whether the active surface is the migrated PIPELINE surface
   * (SE-6071 flag on + Manager+). When true the center region renders the
   * pipeline chip switcher; when false (the default — flag off / sub-Manager)
   * it renders the legacy BoardTabs strip unchanged, so board navigation on
   * the live watchlist/roster surface is never disturbed.
   */
  isPipelineSurface?: boolean;
  /** SE-5506: current pipeline board id — enables the Rules & automation button. */
  boardId?: string;
  /**
   * SE-6488: the pipeline's category chip label (design node 80:14982), or null
   * for a board with no seeded category. Replaces the removed subtitle.
   */
  categoryLabel?: string | null;
  playerCount?: number;
  colors: KanbanColors;
  activeFilterCount?: number;
  isOwner?: boolean;
  userRole?: UserKanbanBoardRole | null;
  /** SE-5491: which renderer the board is currently showing. */
  viewMode?: BoardViewMode;
  /** SE-5491: fired with the newly selected renderer. */
  onViewModeChange?: (mode: BoardViewMode) => void;
  /** SE-6488: board-scoped player search text. */
  boardSearchQuery?: string;
  /** SE-6488: fired as the user types in the toolbar's search input. */
  onBoardSearchChange?: (value: string) => void;
  /** SE-6488: fires the `+ Add player` primary CTA. */
  onAddPlayer?: () => void;
  /* -- SE-6488 toolbar row, supplied by `useBoardToolbar` in the container -- */
  /** Visible label for the Sort trigger (bare, or `Sort: <field>`). */
  sortLabel?: string;
  /** Whether a field is sorted — drives the trigger's accent + arrow. */
  isSortActive?: boolean;
  /** Active sort direction, for the trigger's arrow glyph. */
  sortDirection?: BoardSortDirection;
  /** Sort menu rows. */
  sortOptions?: readonly SelectDropdownOption[];
  /** Whether the sort menu is open. */
  isSortOpen?: boolean;
  /** Toggles the sort menu. */
  onSortToggle?: () => void;
  /** Closes the sort menu. */
  onSortClose?: () => void;
  /** Preserved Stages / Share / Tags rows. */
  overflowItems?: readonly DesktopDropdownMenuItem[];
  /** Whether the overflow menu is open. */
  isOverflowOpen?: boolean;
  /** Toggles the overflow menu. */
  onOverflowToggle?: () => void;
  /** Closes the overflow menu. */
  onOverflowClose?: () => void;
  /** Whether the manager-facing Activity button should render. */
  canSeeActivity?: boolean;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditChange: (value: string) => void;
  onBoardSelect: (boardId: string) => void;
  onCreateBoard: () => void;
  onMoreBoardOptions: () => void;
  onRenameBoard?: () => void;
  onDeleteBoard?: () => void;
  onFilterPress?: () => void;
}

const noOp = () => null;

/**
 * Shared trigger chrome for the toolbar's Sort + overflow controls.
 *
 * SE-6554 item I: the design (80:15018/80:15019, and the search field beside
 * them) draws all three toolbar controls with the SAME border — border300,
 * #d3d3d3, which is exactly `outline-strong`. Dev drew them a step lighter on
 * `outline-subtle` (outline-100, #e6e6e6). The hover follows the repo's
 * established `web:hover:bg-surface-*` precedent (PipelineChipsRow,
 * PipelineDrawerRow) — the design's component set carries an isHovered variant,
 * and a surface lift is the sanctioned way this codebase expresses it.
 */
const TOOLBAR_TRIGGER_CLASS =
  "web:hover:bg-surface-subtle h-9 flex-row items-center justify-center rounded-sm border border-outline-strong bg-surface-base px-4";

/**
 * Trigger visual for the Sort control — mirrors the outline secondary buttons
 * beside it so the toolbar's left group reads as one control set.
 *
 * ## Why this carries an icon when node 80:15019 draws plain text
 *
 * An earlier SE-6554 pass made this label-only and explicitly removed a leading
 * slider glyph dev had carried, because the Figma frame draws Sort as a plain
 * text pill like Filters beside it. That removal was correct: the glyph was
 * decoration that said nothing.
 *
 * The arrow here is the opposite kind of mark. It is STATE — which way the sort
 * runs — and without it the direction is invisible until you reopen the menu.
 * Raniel asked for it directly on 2026-08-06, naming the global player search's
 * trigger as the reference ("use o que já tem"), so the board now reads
 * `<field> ↑` in the accent colour when sorted and a bare neutral `Sort` when
 * not, exactly as `SortTriggerView` does.
 *
 * Recorded because the frame still shows plain text: this is a deliberate,
 * requested deviation from 80:15019, not drift to be tidied away.
 * @param label - The sort trigger label (bare `Sort`, or the active field).
 * @param isActive - Whether a field is sorted.
 * @param direction - The active sort direction.
 * @returns The trigger content.
 */
const renderSortTrigger = (
  label: string,
  isActive: boolean,
  direction: BoardSortDirection
): React.ReactElement => (
  <Stack direction="horizontal" className="items-center gap-2">
    <Text
      variant="body-strong"
      className={isActive ? "text-accent-primary" : "text-content-primary"}
    >
      {label}
    </Text>
    {isActive ? (
      <Icon
        as={direction === "asc" ? ArrowUpIcon : ArrowDownIcon}
        size="sm"
        className="text-accent-primary"
      />
    ) : null}
  </Stack>
);

/**
 * Top panel UI for the kanban board — a TWO-ROW header (SE-6488).
 *
 * ## Layout
 * Row 1 (title / navigation, design node 80:14976) is itself two stacked
 * bands: the pipeline name + category chip + rename affordance on the left with
 * the Kanban|Table segmented control pinned FAR RIGHT, then the pipeline chip
 * switcher (or the legacy BoardTabs strip off the pipeline surface) beneath it.
 * Row 2 is the board toolbar (design node 80:15015), driven by `useBoardToolbar`.
 *
 * ## What moved out of the title row
 * The title row previously carried seven equal-weight controls (Review queue,
 * Activity, Rules, Stages, Filter, Share, Tags) plus the view toggle inline.
 * Review / Activity / Rules now live in the toolbar as secondary buttons;
 * Filter became the toolbar's `Filters`; Stages / Share / Tags are PRESERVED in
 * the toolbar's overflow menu pending the design/product answer recorded in
 * SE-6488's Open Questions. Nothing was deleted.
 *
 * ## Subtitle
 * The design has no descriptive subtitle (node 80:14983 is `hidden`), so the
 * "Recruitment Pipeline Overview" line is gone and the category chip carries
 * that meaning instead. A ROSTER board keeps its `N players` count, which is
 * live data rather than the static pipeline blurb the design dropped.
 * @param root0 - The component props
 * @param root0.pipelineName - The current pipeline/board name to display
 * @param root0.isEditing - Whether the pipeline name is currently being edited
 * @param root0.editValue - The current value in the pipeline name edit field
 * @param root0.boards - Array of available kanban boards for tab selection
 * @param root0.hasUnreachableBoards - Whether boards exist beyond those loaded (SE-6575)
 * @param root0.isPipelineSurface - Whether the active surface is the PIPELINE surface (SE-5489); when true renders the chip switcher, else the legacy BoardTabs
 * @param root0.boardId - Current pipeline board id (enables the Rules button)
 * @param root0.activeFilterCount - Number of currently active filters to display on badge
 * @param root0.userRole - The current user's role on this kanban board
 * @param root0.onStartEdit - Callback fired when user clicks edit button to rename pipeline
 * @param root0.onSaveEdit - Callback fired when user saves the pipeline name edit
 * @param root0.onCancelEdit - Callback fired when user cancels the pipeline name edit
 * @param root0.onEditChange - Callback fired when pipeline name input value changes
 * @param root0.onBoardSelect - Callback fired when user selects a different board tab
 * @param root0.onCreateBoard - Callback fired when user clicks create new board button
 * @param root0.onMoreBoardOptions - Callback fired when user clicks board options menu
 * @param root0.onRenameBoard - Callback fired when user selects rename board option
 * @param root0.onDeleteBoard - Callback fired when user selects delete board option
 * @param root0.onFilterPress - Callback fired when user clicks filter button to open the view's filter drawer
 * @param root0.colors - Theme colors for styling the panel components
 * @param root0.isOwner - Whether current user is the board owner with full permissions
 * @param root0.isRosterBoard - Whether this is a roster board
 * @param root0.playerCount - Current number of players on the board
 * @param root0.viewMode - Which renderer the board is currently showing (SE-5491)
 * @param root0.onViewModeChange - Callback fired with the newly selected renderer (SE-5491)
 * @param root0.categoryLabel - The pipeline's category chip label (SE-6488)
 * @param root0.boardSearchQuery - Board-scoped player search text (SE-6488)
 * @param root0.onBoardSearchChange - Callback fired as the user types in board search (SE-6488)
 * @param root0.onAddPlayer - Callback fired by the `+ Add player` primary CTA (SE-6488)
 * @param root0.sortLabel - Visible label for the Sort trigger (SE-6488)
 * @param root0.isSortActive - Whether a field is sorted (SE-6554)
 * @param root0.sortDirection - Active sort direction for the trigger arrow (SE-6554)
 * @param root0.sortOptions - Sort menu rows (SE-6488)
 * @param root0.isSortOpen - Whether the sort menu is open (SE-6488)
 * @param root0.onSortToggle - Toggles the sort menu (SE-6488)
 * @param root0.onSortClose - Closes the sort menu (SE-6488)
 * @param root0.overflowItems - Preserved Stages / Share / Tags rows (SE-6488)
 * @param root0.isOverflowOpen - Whether the overflow menu is open (SE-6488)
 * @param root0.onOverflowToggle - Toggles the overflow menu (SE-6488)
 * @param root0.onOverflowClose - Closes the overflow menu (SE-6488)
 * @param root0.canSeeActivity - Whether the Activity button renders (SE-6488)
 */
const TopPanelView = ({
  pipelineName,
  isEditing,
  editValue,
  boards,
  hasUnreachableBoards = false,
  isRosterBoard,
  isPipelineSurface = false,
  boardId,
  categoryLabel,
  playerCount = 0,
  colors,
  activeFilterCount = 0,
  isOwner = false,
  userRole,
  viewMode = "kanban",
  onViewModeChange,
  boardSearchQuery = "",
  onBoardSearchChange,
  onAddPlayer,
  sortLabel = "Sort",
  isSortActive = false,
  sortDirection = "asc",
  sortOptions = [],
  isSortOpen = false,
  onSortToggle,
  onSortClose,
  overflowItems = [],
  isOverflowOpen = false,
  onOverflowToggle,
  onOverflowClose,
  canSeeActivity = false,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditChange,
  onBoardSelect,
  onCreateBoard,
  onMoreBoardOptions,
  onRenameBoard,
  onDeleteBoard,
  onFilterPress,
}: TopPanelViewProps) => (
  /* Board container padding per Figma 80:15014 (the 1372px frame holding the
     toolbar and the lanes row, both at x=32): 32px left and right. The lanes
     row in MainView carries the same 32 so the two line up.

     TWO BANDS, not one (Raniel, 2026-08-07): the separator rule closes the
     TITLE band and the toolbar sits BELOW it, directly above the content. The
     panel used to be a single bordered box, which put the rule between the
     toolbar and the board — `toolbar, rule, table` where he wants
     `rule, toolbar, table`. The rule is the title/navigation band's own
     `border-b`, so it still runs edge to edge (the horizontal inset lives on
     each band, never on this wrapper) without a negative-inset trick. */
  <Box className="z-50 w-full overflow-visible bg-surface-base">
    {/* ── TITLE BAND — name/chips/navigation, closed by the separator rule ──
        space="4" (16px) is the one gap the repo spaces every TopPanel row
        with; the design splits these rows across two frames and cannot say
        what that gap should be. */}
    <Box className="w-full overflow-visible border-b border-b-outline-subtle px-8 pb-4 pt-5">
      <Stack space="4" className="w-full overflow-visible">
        {/* ── ROW 1a — title + rename, view toggle pinned far right ── */}
        <Stack
          direction="horizontal"
          className="w-full items-center justify-between overflow-visible"
        >
          <Stack
            direction="horizontal"
            className="min-w-0 flex-1 items-center gap-2"
          >
            {isEditing ? (
              <Stack direction="horizontal" className="items-center gap-2">
                <Input
                  className="w-64 rounded-sm border"
                  // eslint-disable-next-line no-restricted-syntax -- data-driven KanbanColors bg/border (columnBackground/primary); no closed token expresses these theme colors
                  UNSAFE_style={renameInputStyle(colors)}
                  // The rename field now renders the same text style as the
                  // pipeline name it replaces (the non-editing branch below is
                  // `Text variant="title-lg"`), instead of a raw size+weight
                  // pair on an untyped inherited family.
                  textVariant="title-lg"
                  field={{
                    testID: TEST_ID_PLAYER_KANBAN.RENAME_BOARD_INPUT,
                    value: editValue,
                    onChangeText: onEditChange,
                    className: "px-3 py-2",
                    style: textColorStyle(colors.textPrimary),
                    placeholderTextColor: colors.textMuted,
                  }}
                />
                {/* SE-6799: Save and cancel use semantic status styling so they
                    respond to the active theme. */}
                <Pressable
                  onPress={onSaveEdit}
                  accessibilityLabel="Save board name"
                  testID={TEST_ID_PLAYER_KANBAN.RENAME_BOARD_SAVE_BUTTON}
                  className="rounded-sm bg-status-success-surface p-2"
                >
                  <Icon as={Check} size="sm" className="text-status-success" />
                </Pressable>
                <Pressable
                  onPress={onCancelEdit}
                  accessibilityLabel="Cancel board rename"
                  testID={TEST_ID_PLAYER_KANBAN.RENAME_BOARD_CANCEL_BUTTON}
                  className="rounded-sm bg-status-error-surface p-2"
                >
                  <Icon as={X} size="sm" className="text-status-error" />
                </Pressable>
              </Stack>
            ) : (
              <>
                <Box testID={TEST_ID_PLAYER_KANBAN.PIPELINE_NAME}>
                  <Text variant="title-lg" className="text-content-primary">
                    {pipelineName}
                  </Text>
                </Box>

                {/* SE-6488: category tag chip (design node 80:14982). Rendered
                  only when the pipeline actually has a seeded category — never
                  filled with a placeholder. */}
                {categoryLabel ? (
                  <Badge
                    testID={TEST_ID_PIPELINE_CATEGORY_CHIP.ROOT}
                    action="info"
                    variant="solid"
                    size="sm"
                  >
                    <BadgeText>{categoryLabel}</BadgeText>
                  </Badge>
                ) : null}

                {userRole != null && <BoardRoleBadge role={userRole} />}

                {/* Rename-pipeline trigger, per Figma 80:14987 — a borderless
                  36×36 button on background0 carrying a 12px edit glyph, sitting
                  immediately after the pipeline name and its category badge. The
                  node is a Select instance with its dropdown hidden, so only the
                  icon renders; its Code Connect mapping gives the icon verbatim.
                  px-3 either side of a 12px glyph is the 36px width; h-9 is the
                  height. Dev had a bordered ~22px chip on surface-raised.
                  It opens the inline rename editor above (useBoardNameEditing),
                  which persists through UpdateKanbanBoard. */}
                {isOwner && (
                  <Pressable
                    onPress={onStartEdit}
                    accessibilityLabel="Rename pipeline"
                    className="h-9 flex-row items-center rounded-sm bg-surface-base px-3"
                  >
                    <Icon
                      as={EditIcon}
                      size="2xs"
                      className="text-content-primary"
                    />
                  </Pressable>
                )}

                {/* SE-6488: the design drops the static "Recruitment Pipeline
                  Overview" subtitle (node 80:14983 is hidden) in favour of the
                  category chip above. A ROSTER board keeps its live player
                  count — that is data, not the pipeline blurb. */}
                {isRosterBoard ? (
                  <Text variant="caption" className="text-content-muted">
                    {`${playerCount} players`}
                  </Text>
                ) : null}
              </>
            )}
          </Stack>

          {/* SE-5491 / SE-6488: Kanban ⇄ Table switch, pinned to the FAR RIGHT of
            the header (design node 80:14988 sits at the container's right
            edge), not inline in the middle of the action row. */}
          {onViewModeChange ? (
            <Box className="shrink-0">
              <BoardViewToggle value={viewMode} onChange={onViewModeChange} />
            </Box>
          ) : null}
        </Stack>

        {/* ── ROW 1b — pipeline navigation ──
          SE-5489: on the migrated PIPELINE surface (SE-6071 flag on +
          Manager+) this renders the pipeline chip switcher + all-pipelines
          drawer. On every other surface it renders the legacy BoardTabs strip
          UNCHANGED — the pipeline surface ships behind a flag that defaults off,
          so gating here (rather than replacing BoardTabs unconditionally) keeps
          board navigation intact on the live watchlist/roster surface. Because
          PipelineChipsRow only mounts when `isPipelineSurface` is true, its
          PipelineSwitcherBoards / recency / drawer reads never fire off-surface
          — the fetch is gated on surface-resolution (data-readiness), not on
          transient UI visibility.
          SE-6488 moved this onto its own line: in the design the switcher is a
          full-width navigation band beneath the title, not a centre column
          squeezed between the title and the action buttons. */}
        <Box className="z-50 w-full overflow-visible">
          {isPipelineSurface ? (
            <PipelineChipsRow
              activeBoardId={boardId ?? null}
              onSelectPipeline={onBoardSelect}
              // SE-6537: the SAME creation callback the legacy branch below has
              // always received. SE-5489 forwarded only selection here, which
              // left `usePipelineCreationFlow` intact but unreachable — no user,
              // regardless of role, could create a pipeline from the UI.
              onCreatePipeline={onCreateBoard}
            />
          ) : (
            <BoardTabs
              boards={boards}
              hasUnreachableBoards={hasUnreachableBoards}
              onBoardSelect={onBoardSelect}
              onCreateBoard={onCreateBoard}
              onMoreOptions={onMoreBoardOptions}
              onRenameBoard={onRenameBoard}
              onDeleteBoard={onDeleteBoard}
              canEdit={isOwner}
              userRole={userRole}
            />
          )}
        </Box>
      </Stack>
    </Box>

    {/* ── TOOLBAR BAND (design node 80:15015) — BELOW the separator ──
        The seven equal-weight controls that used to crowd the title row now
        live here with real hierarchy: search / Filters / Sort on the left,
        Review / Activity / Rules as secondary buttons on the right, the
        preserved Stages / Share / Tags behind the overflow menu, and
        `+ Add player` as the single filled primary CTA.

        `pt-4` is the 16px the rule owes the toolbar; there is deliberately NO
        bottom padding, because both renderers below (PipelineTableView and
        MainView's kanban branch) already open with their own `pt-4`. Adding one
        here would double the gap to 32. */}
    <Box className="relative w-full overflow-visible px-8 pt-4">
      {/* SE-7710: the row reflows below the md breakpoint (768px) instead of
          running off the edge. Measured on staging, the row needs 440px of
          content in a 326px box at 390 — and because the left group is the
          only flexible item, that shortfall was spent entirely on IT: the
          search field, Filters and Sort were squeezed to width 0 (absent, not
          clipped) while `Add player` was pushed to x=348..472 and clipped away
          by the screen container's `overflow-x: hidden`.

          Reflow, not a horizontal scroller. The lane container immediately
          below is already a real horizontal scroller (measured `overflow-x:
          scroll`, scrollWidth 1808, pans 0 -> 1482 at 390), so a second one
          stacked directly above it would make a sideways swipe ambiguous
          between two adjacent scrollers. A scroller also only rescues `Add
          player` — the width-0 left group has no basis to grow from either
          way, so the search field would stay unusable.

          The gate is screen width and nothing else: `md:` is NativeWind's
          768px viewport breakpoint, the same threshold as `useIsMobile()`.
          Expressed as breakpoint classes rather than a hook so the >= 768
          row is unchanged — `md:basis-0` restores exactly what `flex-1`
          (`flex: 1 1 0%`) supplied, and `md:flex-nowrap` restores the single
          row. Unrelated to, and deliberately separate from, the `@dnd-kit`
          platform-capability gating this feature carries elsewhere. */}
      <Stack
        direction="horizontal"
        testID={TEST_ID_BOARD_TOOLBAR.ROOT}
        className="w-full flex-wrap items-center gap-2 overflow-visible md:flex-nowrap"
      >
        {/* Left group — search, Filters, Sort (design node 80:15016).
            `grow shrink basis-full` is the long form of `flex-1` with the
            basis swapped: below md the group takes a line of its own (so the
            search field renders at its full width instead of collapsing),
            and `md:basis-0` puts it back to `flex-1`'s `0%` at desktop.

            `flex-wrap` here carries NO `md:` counterpart, deliberately. This
            group is the row's only flexible item, so it is where a shortfall
            lands at any width — and it is `overflow-visible`, so its children
            do not clip when they no longer fit, they PAINT OVER the buttons
            beside them. Measured on the pre-fix build at 768: Filters sat at
            x=296..372 underneath Review at x=304..382 and Sort at x=380..440
            underneath Activity at x=390..470, so neither answered a hit test
            at its own centre. Wrapping unconditionally puts Filters and Sort
            on a second line whenever the group is short of room — which never
            happens above ~910px, where the group has 408px of content and more
            than that available, so 1280 and 1920 keep the single row they had.
            The 768 change is from two unclickable controls to two clickable
            ones; clipping there was 0 before and stays 0. */}
        <Stack
          direction="horizontal"
          className="shrink grow basis-full flex-wrap items-center gap-2 overflow-visible md:basis-0"
        >
          {/* SE-6554: design node 80:15017 fixes the field at 240px. 240 is
                not a rung on the closed sizing ladder, so cap at the nearest
                one (64 = 256px) rather than mint a token for a 16px delta. */}
          <Box className="w-full max-w-64">
            <Input
              size="sm"
              leadingIcon={Search}
              leadingIconTone="muted"
              field={{
                testID: TEST_ID_BOARD_TOOLBAR.SEARCH_INPUT,
                accessibilityLabel: "Search players on this board",
                placeholder: "Search players",
                value: boardSearchQuery,
                onChangeText: onBoardSearchChange ?? noOp,
              }}
            />
          </Box>

          <Button
            label={
              activeFilterCount > 0
                ? `Filters (${activeFilterCount})`
                : "Filters"
            }
            variant="outline"
            tone="secondary"
            size="sm"
            labelTone="primary"
            /*
             * SE-6554 item I — INTERIM CALL-SITE WAIVER, authorised by Raniel
             * for visual correctness now (design node 80:15163).
             *
             * The node draws this button with the same border the search field
             * and Sort carry: border300 = #d3d3d3 = `outline-strong`. The
             * Button atom's outline border comes from its gluestack action and
             * has no tone axis, and the outline variant has no hover axis, so
             * neither is expressible through the atom's vocabulary. Both are
             * closed tokens, so this overrides nothing raw — but it IS a
             * call-site override of atom-owned chrome, and the real fix is a
             * border-tone (+ hover) axis on the atom. Keep that ticket open;
             * remove this line when it lands.
             *
             * NOT applied from the node's Code Connect props: they say
             * `shape="pill"` and `tone="primary"`, but the rendered node — and
             * the toolbar row 80:15016 — show a small-radius rectangle with a
             * neutral grey border. Two different node ids (80:15018, 80:15163)
             * return byte-identical props, which is the component's default
             * binding rather than this instance's variant. Screenshot wins.
             */
            className="web:hover:bg-surface-subtle border-outline-strong"
            accessibilityLabel="Open board filters"
            testID={TEST_ID_BOARD_TOOLBAR.FILTERS_BUTTON}
            onPress={onFilterPress ?? noOp}
          />

          <SelectDropdown
            isOpen={isSortOpen}
            onClose={onSortClose ?? noOp}
            onToggle={onSortToggle ?? noOp}
            triggerContent={renderSortTrigger(
              sortLabel,
              isSortActive,
              sortDirection
            )}
            triggerTestID={TEST_ID_BOARD_TOOLBAR.SORT_BUTTON}
            triggerAccessibilityLabel="Sort players on this board"
            triggerClassName={TOOLBAR_TRIGGER_CLASS}
            options={sortOptions}
          />
        </Stack>

        {/* Right group — secondary panels, then the primary CTA. Design node
              order: Review, Activity, Rules, Add player. */}
        <ReviewQueueButton kanbanBoardId={boardId} />

        {canSeeActivity ? (
          <PipelineActivityLogButton kanbanBoardId={boardId} />
        ) : null}

        {isRosterBoard === false ? (
          <RulesPanelButton kanbanBoardId={boardId} />
        ) : null}

        {/* SE-6488 open question: `Stages`, `Share` and `Tags` exist on dev
              but are absent from the design frame. They are PRESERVED here —
              reachable, not deleted — pending a design/product answer, rather
              than dropped on the implementer's own authority. See the ticket's
              Open Questions. */}
        {overflowItems.length > 0 ? (
          <DesktopDropdownMenu
            isOpen={isOverflowOpen}
            onClose={onOverflowClose ?? noOp}
            onToggle={onOverflowToggle ?? noOp}
            triggerContent={
              <Icon
                as={MoreHorizontal}
                size="sm"
                className="text-content-secondary"
              />
            }
            triggerTestID={TEST_ID_BOARD_TOOLBAR.OVERFLOW_MENU}
            triggerAccessibilityLabel="More board actions"
            triggerClassName={TOOLBAR_TRIGGER_CLASS}
            items={overflowItems}
          />
        ) : null}

        <Button
          label="Add player"
          icon={Plus}
          tone="accent"
          size="sm"
          /* SE-6865: toolbar radius + label weight, set at the call site on
             purpose — see the anchor note in ReviewQueueButtonContainer for the
             two atom-level defects this deliberately does NOT fix. */
          shape="rounded"
          labelVariant="body-strong"
          accessibilityLabel="Add player to this pipeline"
          testID={TEST_ID_BOARD_TOOLBAR.ADD_PLAYER_BUTTON}
          onPress={onAddPlayer ?? noOp}
        />
      </Stack>
    </Box>
  </Box>
);

TopPanelView.displayName = "TopPanelView";

export default memo(TopPanelView);
